import { Helmet } from 'react-helmet-async';
import { useCallback, useEffect, useState } from 'react';
// @mui
import { Button, Container, Stack, Typography } from '@mui/material';
// components
import { StandardsList } from '../sections/@dashboard/standards';
import RuleEditorDialog from '../sections/@dashboard/rules/RuleEditorDialog';
import PresetPickerDialog from '../sections/@dashboard/rules/PresetPickerDialog';
import { createRule, deleteRule, getAllRules } from '../services/ruleService';
import Iconify from '../components/iconify';

/**
 * Rules — create, edit and delete them, or install a prebuilt pack.
 *
 * Replaces StandardsPage. The old page carried two dialogs built around the flat
 * {sensorType, operator, value} triple; both are gone, since a rule is now an
 * expression and the editor is a view of that one string.
 */
export default function RulesPage() {
  const [rules, setRules] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setRules(await getAllRules());
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (rule) => {
    // Let the error propagate: the editor turns a server rejection into an
    // inline message with the offending character, which is more use than a toast.
    await createRule(rule);
    await load();
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    await deleteRule(ruleId);
    await load();
  };

  const openEditor = (rule) => {
    setEditing(rule ?? null);
    setEditorOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>VPSI | Rules</title>
      </Helmet>
      <Container>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Rules
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 5 }}>
          <Button variant="contained" onClick={() => openEditor(null)} startIcon={<Iconify icon="eva:plus-fill" />}>
            Create New Rule
          </Button>
          <Button variant="outlined" onClick={() => setPresetsOpen(true)} startIcon={<Iconify icon="eva:cube-outline" />}>
            Add a prebuilt pack
          </Button>
        </Stack>
        <StandardsList
          standards={rules}
          onEdit={openEditor}
          onDelete={handleDelete}
          onImageClick={openEditor}
        />
      </Container>

      <RuleEditorDialog
        open={editorOpen}
        rule={editing}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
      />
      <PresetPickerDialog
        open={presetsOpen}
        onClose={() => setPresetsOpen(false)}
        onInstalled={load}
      />
    </>
  );
}
